<template>
  <aside
    class="flex h-dvh w-fit flex-col bg-[--v3-theme-primarycolor] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.10),0px_8px_10px_-6px_rgba(0,0,0,0.10)] transition-[margin,box-shadow] duration-150"
    :class="{
      'ml-[-277px] !shadow-none': isMobile && collapsed,
      'hidden': !isMobile && !authStore.isUserLogged
    }"
  >
    <a :href="dashboardURL">
      <ErankLogo class="flex-none" :collapsed="collapseState" :plan="subscriptionStore.currentPlan" />
    </a>
    <div class="frs-hide-scroll relative grow overflow-y-scroll">
      <nav class="h-full">
        <template v-for="(route, index) in routesList" :key="index">
          <template v-if="'type' in route">
            <div class="bg-[--v3-theme-primarycolor] px-[20px] py-[15px]">
              <hr class="divider m-0 border-t border-[--v3-theme-primarydarkercolor]">
            </div>
          </template>
          <template v-else-if="'items' in route">
            <NavMenuGroup
              :text="route.text"
              :icon="route.icon"
              :routes="route.items"
              :active="openMenu === route.text"
              :active-route-name="currentRouteName"
              :collapsed="collapseState"
              @toggle-menu="handleToggleMenu($event)"
            >
              <NavMenuGroupItem
                v-for="(routeItem , routeIndex) in route.items"
                :key="`route-${routeIndex}`"
                :to="routeItem.to"
                :legacy-url="routeItem.legacy"
                :name="routeItem.text"
                :active="routeItem.parentRouteName === currentRouteName"
                :collapsed="collapseState"
              />
            </NavMenuGroup>
          </template>
          <template v-else>
            <NavMenuItem
              :text="route.text"
              :icon="route.icon"
              :legacy-url="route.legacy"
              :to="route.to"
              :active="currentRouteName === route.to"
              :collapsed="collapseState"
              :target="route.target"
            />
          </template>
        </template>
      </nav>
    </div>
    <UpgradeButton :collapsed="collapseState" :plan="subscriptionStore.currentPlan" />
    <div
      class="flex flex-none"
      :class="{
        '-mt-px border border-x-0 border-[--v3-theme-primarycolor] pl-[225px]': !authStore.isUserLogged
      }"
    >
      <UserProfile
        v-if="authStore.isUserLogged"
        :avatar="authStore.user.current_shop?.image_url_75x75"
        :collapsed="collapseState"
        :is-loading="isPendingShops"
        :name="shop?.name"
      >
        <UserSettings />
      </UserProfile>
      <CollapseButton
        :class="{
          '-mt-px': !authStore.isUserLogged
        }"
        :collapsed="collapseState"
        @toggle-collapse="handleToggleCollapse($event)"
      />
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useMobile } from '@/common/composables/application';
import { useAuthStore } from '@/common/stores/auth';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { storage } from '@/helpers/local-storage';
import CollapseButton from '@/modules/layout/components/atoms/CollapseButton';
import ErankLogo from '@/modules/layout/components/atoms/ErankLogo';
import NavMenuGroupItem from '@/modules/layout/components/atoms/NavMenuGroupItem';
import NavMenuItem from '@/modules/layout/components/atoms/NavMenuItem';
import UpgradeButton from '@/modules/layout/components/atoms/UpgradeButton';
import NavMenuGroup from '@/modules/layout/components/molecules/NavMenuGroup';
import UserProfile from '@/modules/layout/components/molecules/UserProfile';
import UserSettings from '@/modules/layout/components/organisms/UserSettings';
import { useSidebarStore } from '@/modules/layout/stores/sidebar';
import { useShops } from '@/queries/shop';

import { publicRoutes, registeredRoutes } from './menu-routes';

const { isMobile } = useMobile();

const openMenu = ref(null);
const sidebarStore = useSidebarStore();
const { toggleSidebar } = sidebarStore;
const { collapsed, sideBarCollapsePreference } = storeToRefs(sidebarStore);
const route = useRoute();
const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const dashboardURL = '/dashboard';
const { data: shops, isPending: isPendingShops } = useShops();

const collapseState = computed<boolean>(() => isMobile.value ? false : collapsed.value);
const currentRouteName = computed<string>(() => route.matched[0]?.name as string);
const routesList = computed(() => authStore.isUserLogged ? registeredRoutes : publicRoutes);
const shop = computed(() => shops.value?.find(shop => shop.id === authStore.user.current_shop_id));

const handleToggleMenu = (route: string) => {
  openMenu.value = (collapsed.value || openMenu.value !== route) ? route : null;
  if (collapsed.value) {
    collapsed.value = false;
    toggleSidebar();
  }
};

const handleToggleCollapse = (value: boolean) => {
  collapsed.value = value;
  toggleSidebar();
};

onMounted(() => {
  // Detect the route and open the menu
  if (!isMobile.value) {
    document.body.style.setProperty('--sidebar-width', collapsed.value ? '50px' : '277px');
  }

  routesList.value.forEach((route) => {
    if ('items' in route) {
      route.items.forEach((item) => {
        if (item.parentRouteName === currentRouteName.value) {
          openMenu.value = route.text;
        }
      });
    } else if ('parentRouteName' in route) {
      if (route.parentRouteName === currentRouteName.value) {
        openMenu.value = route.text;
      }
    }
  });
});

watch(() => currentRouteName.value, () => {
  if(isMobile.value) {
    handleToggleCollapse(true);
  }
}, { immediate: true });

watch(() => sideBarCollapsePreference.value, (newValue) => {
  storage.set<boolean>('sidebar-collapsed', newValue === undefined ? false : newValue);
}, { immediate: true });
</script>
